import { domain } from "@relode/core";

import { converters } from ".";
import { JobDetails, JobOverview, Order, Team, TeamMember } from "../../types";
import {
    JobDetailsResponse,
    JobOverviewResponse,
    JobTeamResponse,
    OrderResponse,
    TeamMemberResponse,
} from "../controllers/jobs";
import { convertJobQualificationsResponse } from "./convertJobQualificationsResponse";

/* eslint-disable max-len */
const convertJobOverviewResponse = (
    response: JobOverviewResponse
): JobOverview => {
    if (!response) return null;

    const overview: JobOverview = {
        jobId: response.jobId,
        jobName: response.jobName,
        normalizedJobName: response.normalizedJobName,
        teamName: response.teamName,
        jobType: domain.JobType.fromId(response.jobTypeId),
        jobPriorityType: domain.JobPriorityType.fromId(
            response.jobPriorityTypeId
        ),
        contractStartDate: response.contractStartDate,
        contractEndDate: response.contractEndDate,
        hoursPerWeek: response.hoursPerWeek,
        jobSalaryTypeId: response.jobSalaryTypeId,
        jobSalaryInUSDollars: response.jobSalaryInUSDollars,
        salaryDescription: response.salaryDescription,
        jobStatus: domain.JobStatus.fromId(response.jobStatusTypeId),
        jobStatusMessage: response.jobStatusMessage,
        statusReasonTimestamp: response.statusReasonTimestamp,
        location: converters.convertLocationResponse(response.location),
        normalizedLocation: converters.convertLocationResponse(
            response.normalizedLocation
        ),
        openPositionCount: response.openPositionCount,
        billRateInUSDollars: response.billRateInUSDollars,
        jobManager: converters.convertJobManagerContactResponseNonStakeholder(
            response.jobManager
        ),
        attachments: converters.convertFileResponseList(response.attachments),
        overview: response.overview,
        externalDescription: response.externalDescription,
        isIntegrated: response.isIntegrated,
        canViewSalary: response.canViewSalary,
        canShare: response.canShare,
        canCopy: response.canCopy,
        canEdit: response.canEdit,
        showMarketResearchData: response.canAccessMarketResearch,
        showMarketResearchMarketing:
            response.canUpsellMarketResearch &&
            !response.canAccessMarketResearch,

        primaryJobPosition: converters.convertJobPositionResponse(
            response.primaryJobPosition
        ),
        secondaryJobPositions: converters.convertJobPositionResponseList(
            response.secondaryJobPositions
        ),
        benefits: converters.convertTagTypeResponseList(response.benefits),
        hasSignedAgreement: response.hasSignedAgreement,
        teamHasActiveTeamPlan: response.teamHasActiveTeamPlan,
        jobAgreementHasActiveTeamPlan: response.jobAgreementHasActiveTeamPlan,
        totalAvailableCandidateCount: response.totalAvailableCandidateCount,
    };
    return overview;
};

const convertTeamMembersResponse = (
    response: TeamMemberResponse[]
): TeamMember[] => {
    if (!response) return null;
    return response.map(t => convertTeamMemberResponse(t));
};

const convertTeamMemberResponse = (
    response: TeamMemberResponse
): TeamMember => {
    if (!response) return null;

    const teamMember: TeamMember = new TeamMember();
    teamMember.userId = response.userId;
    teamMember.fullName = response.fullName;
    teamMember.email = response.email;
    teamMember.jobUserTypeId = response.jobUserTypeId;
    teamMember.teamName = response.teamName;
    teamMember.createTimestamp = response.createTimestamp;
    teamMember.isSelf = response.isSelf;
    teamMember.candidatesAwaitingFeedbackCount =
        response.candidatesAwaitingFeedbackCount;

    return teamMember;
};

const convertTeamResponse = (response: JobTeamResponse): Team => {
    if (!response) return null;

    const team: Team = new Team();
    team.teamMembers = convertTeamMembersResponse(response.teamMembers);
    team.canManageTeam = response.canManageTeam;
    team.companyName = response.companyName;
    team.companyUrl = response.companyUrl;
    team.companyAddress1 = response.companyAddress1;
    team.companyAddress2 = response.companyAddress2;
    team.companyBackground = response.companyBackground;
    team.companyLocationName = response.companyLocationName;

    return team;
};

const convertOrderResponse = (
    response: OrderResponse,
    canView: boolean
): Order => {
    if (!response) return null;
    const order: Order = {
        quantity: response.quantity,
        planName: response.planName,
        employmentGuarantee: domain.EmploymentGuaranteeType.fromId(
            response.employmentGuaranteeTypeId
        ),
        employmentGuaranteeDescription: response.employmentGuaranteeDescription,
        employmentGuaranteePayment: response.employmentGuaranteePayment,
        canViewEmploymentGuarantee: canView,
        postingFeeInUSDollars: response.postingFeeInUSDollars,
        successFeePercentage: response.successFeePercentage,
        successFeeInUSDollars: response.successFeeInUSDollars,
        successFeeSavingsInUSDollars: response.successFeeSavingsInUSDollars,
        defaultPostingFeeInUSDollars: response.defaultPostingFeeInUSDollars,
        marketResearchFeatureType: domain.MarketResearchFeature.fromId(
            response.marketResearchFeatureTypeId
        ),
        defaultJobPriorityASuccessFeePercentage:
            response.defaultJobPriorityASuccessFeePercentage,
        defaultJobPriorityBSuccessFeePercentage:
            response.defaultJobPriorityBSuccessFeePercentage,
        defaultJobPriorityCSuccessFeePercentage:
            response.defaultJobPriorityCSuccessFeePercentage,
        defaultJobPriorityDSuccessFeePercentage:
            response.defaultJobPriorityDSuccessFeePercentage,
        estimatedDefaultJobPriorityASuccessFeeInUSDollars:
            response.estimatedDefaultJobPriorityASuccessFeeInUSDollars,
        estimatedDefaultJobPriorityBSuccessFeeInUSDollars:
            response.estimatedDefaultJobPriorityBSuccessFeeInUSDollars,
        estimatedDefaultJobPriorityCSuccessFeeInUSDollars:
            response.estimatedDefaultJobPriorityCSuccessFeeInUSDollars,
        estimatedDefaultJobPriorityDSuccessFeeInUSDollars:
            response.estimatedDefaultJobPriorityDSuccessFeeInUSDollars,
        estimatedDefaultJobPriorityASuccessFeeSavingsInUSDollars:
            response.estimatedDefaultJobPriorityASuccessFeeSavingsInUSDollars,
        estimatedDefaultJobPriorityBSuccessFeeSavingsInUSDollars:
            response.estimatedDefaultJobPriorityBSuccessFeeSavingsInUSDollars,
        estimatedDefaultJobPriorityCSuccessFeeSavingsInUSDollars:
            response.estimatedDefaultJobPriorityCSuccessFeeSavingsInUSDollars,
        estimatedDefaultJobPriorityDSuccessFeeSavingsInUSDollars:
            response.estimatedDefaultJobPriorityDSuccessFeeSavingsInUSDollars,
        billRateInUSDollars: response.billRateInUSDollars,
        estimatedContractBillTotalInUSDollars:
            response.estimatedContractBillTotalInUSDollars,
        estimatedContractBillTotalSavingsInUSDollars:
            response.estimatedContractBillTotalSavingsInUSDollars,
        hasSignedAgreement: response.hasSignedAgreement,
    };

    return order;
};

export const convertJobDetailsResponse = (
    response: JobDetailsResponse
): JobDetails => {
    const job: JobDetails = new JobDetails();
    job.details = convertJobOverviewResponse(response.details);
    job.team = convertTeamResponse(response.team);
    job.jobQualifications = convertJobQualificationsResponse(
        response.jobQualifications
    );
    job.order = convertOrderResponse(
        response.order,
        response.details.canViewSalary
    );
    job.jobConcurrencyToken = response.jobConcurrencyToken;
    job.actions = response.actions;

    return job;
};
